import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AxiosError, AxiosResponse } from "axios";
import { RootState } from "./store";
import { axiosApi, downloadFile, getPostfixWithModelName } from "../helpers";
import { ENDPOINTS_MODEL, HOST } from "../constants/api_endpoints";
import {
	IDeleteModelProps,
	IImportModelProps,
	IGetAllModelsProps,
	IGetInfoModelProps,
	IModelState,
	AllModelResponseType,
	IGetLogsModelProps,
	IGetErrorsModelProps,
	ModelType,
	IGetHandlerProps,
	IUploadHandlerProps,
	IDeleteHandlerProps,
	IExportModelProps,
	IModelTypes,
	IModel,
	ICopyModelProps,
	ILogModel,
} from "../types/modelTypes";
import { IResponse, ModelStatus, RequestStatus, ResponseStatus } from "../types/statusTypes";

const initialState: IModelState = {
	allModels: {
		models: null,
		additionalModelsList: null,
		cleanListOfModels: [],
		pretrainedModel: [],
		status: RequestStatus.IDLE,
		error: ResponseStatus.SUCCESS,
		handlers: [],
		message: "",
	},
	fullModel: {
		activeType: "future",
		modelName: null,
		fullModel: {
			previous: null,
			current: null,
			future: null,
		},
		training: false,
		testing: false,
		status: RequestStatus.IDLE,
		error: ResponseStatus.SUCCESS,
		message: "",
	},
	modelExport: {
		status: RequestStatus.IDLE,
		error: ResponseStatus.SUCCESS,
		message: "",
	},
	modelImport: {
		status: RequestStatus.IDLE,
		error: ResponseStatus.SUCCESS,
		message: "",
		newName: null,
		progress: 0,
	},
	modelCopy: {
		status: RequestStatus.IDLE,
		error: ResponseStatus.SUCCESS,
		message: "",
	},
	modelDelete: {
		status: RequestStatus.IDLE,
		error: ResponseStatus.SUCCESS,
		message: "",
	},
	modelLogs: {
		status: RequestStatus.IDLE,
		logs: [],
		error: ResponseStatus.SUCCESS,
		message: "",
	},
	modelErrors: {
		status: RequestStatus.IDLE,
		errors: [],
		error: ResponseStatus.SUCCESS,
		message: "",
	},
	handler: {
		status: RequestStatus.IDLE,
		data: null,
	},
	handlerUpload: {
		status: RequestStatus.IDLE,
		error: ResponseStatus.SUCCESS,
		message: "",
	},
	handlerDelete: {
		status: RequestStatus.IDLE,
		error: ResponseStatus.SUCCESS,
		message: "",
	},
};

// получение списка всех моделей
export const getAllModels = createAsyncThunk("model/list", async ({ serviceType }: IGetAllModelsProps, { rejectWithValue }) => {
	try {
		const response: AxiosResponse<AllModelResponseType | IResponse> = await axiosApi.get(`${HOST}/${serviceType}/${ENDPOINTS_MODEL.LIST}`);
		return response.data;
	} catch (error) {
		if (error) {
			// возвращаем данные ошибки, пришедшие с бэка
			return rejectWithValue(error);
		}
	}
});

// получение инфо о модели
export const getInfoModel = createAsyncThunk(
	"model/info",
	async ({ modelName, serviceType, modelNameAsIs }: IGetInfoModelProps, { getState, rejectWithValue, signal }) => {
		try {
			const activeType = (getState() as RootState).model.fullModel.activeType;
			const response: AxiosResponse<IModel | IResponse> = await axiosApi.get(
				`${HOST}/${serviceType}/${ENDPOINTS_MODEL.INFO}/${modelNameAsIs ? modelName : getPostfixWithModelName(activeType, modelName)}`,
				{ signal }
			);
			return response.data;
		} catch (error) {
			if (error) {
				// возвращаем данные ошибки, пришедшие с бэка
				return rejectWithValue(error);
			}
		}
	}
);

// экспорт модели (.zip файл)
export const exportModel = createAsyncThunk("model/export", async ({ modelName, modelType, serviceType }: IExportModelProps, { rejectWithValue }) => {
	try {
		const response: AxiosResponse<Blob | IResponse> = await axiosApi.get(
			`${HOST}/${serviceType}/${ENDPOINTS_MODEL.EXPORT}/${serviceType === "spr" ? modelName : getPostfixWithModelName(modelType, modelName)}`,
			{ responseType: "blob" }
		);
		if (response.data instanceof Blob) {
			downloadFile(response.data, `${serviceType === "spr" ? modelName : getPostfixWithModelName(modelType, modelName)}.zip`);
		} else return response.data;
	} catch (error) {
		if (error) {
			// возвращаем данные ошибки, пришедшие с бэка
			return rejectWithValue(error);
		}
	}
});

// импорт модели
export const importModel = createAsyncThunk("model/import", async ({ modelName, formData, serviceType }: IImportModelProps, { dispatch }): Promise<IResponse> => {
	const response: AxiosResponse<IResponse> = await axiosApi.post(
		`${HOST}/${serviceType}/${ENDPOINTS_MODEL.IMPORT}/${modelName}`,
		{ "zip-model": formData.get("file") },
		{
			headers: {
				"Content-Type": "multipart/form-data",
			},
			onUploadProgress: (progressEvent) => {
				if (progressEvent.progress) dispatch(changeProgressImportModel(progressEvent.progress));
			},
		}
	);
	return response.data;
});

// удаление модели
export const deleteModel = createAsyncThunk("model/delete", async ({ modelName, serviceType }: IDeleteModelProps, { getState, rejectWithValue }) => {
	try {
		const activeType = (getState() as RootState).model.fullModel.activeType;
		const response: AxiosResponse<IResponse> = await axiosApi.delete(
			`${HOST}/${serviceType}/${ENDPOINTS_MODEL.DELETE}/${serviceType === "spr" ? modelName : getPostfixWithModelName(activeType, modelName)}`
		);
		return response.data;
	} catch (error) {
		if (error) {
			// возвращаем данные ошибки, пришедшие с бэка
			return rejectWithValue(error);
		}
	}
});

// копирование модели (для применения, восстановления)
export const copyModel = createAsyncThunk(
	"model/copy",
	async ({ serviceType, sourceModel, destinationModel, backupModel, removeSourceModel }: ICopyModelProps, { rejectWithValue }) => {
		try {
			const response: AxiosResponse<IResponse> = await axiosApi.post(`${HOST}/${serviceType}/${ENDPOINTS_MODEL.COPY}`, {
				source: sourceModel,
				destination: destinationModel,
				backup: backupModel,
				remove: removeSourceModel,
			});
			return response.data;
		} catch (error) {
			if (error) {
				// возвращаем данные ошибки, пришедшие с бэка
				return rejectWithValue(error);
			}
		}
	}
);

// получение логов модели
export const getLogModel = createAsyncThunk("model/log", async ({ modelName, modelType, serviceType }: IGetLogsModelProps, { rejectWithValue }) => {
	try {
		const response: AxiosResponse<ILogModel | IResponse> = await axiosApi.get(
			`${HOST}/${serviceType}/${ENDPOINTS_MODEL.LOG}/${getPostfixWithModelName(modelType, modelName)}`
		);
		return response.data;
	} catch (error) {
		if (error) {
			// возвращаем данные ошибки, пришедшие с бэка
			return rejectWithValue(error);
		}
	}
});

// получение ошибок модели
export const getErrorsModel = createAsyncThunk(
	"model/errors",
	async ({ modelName, modelType, serviceType, typeErrors = "train" }: IGetErrorsModelProps, { rejectWithValue }) => {
		try {
			const response: AxiosResponse<[string, string, string][] | IResponse> = await axiosApi.get(
				`${HOST}/${serviceType}/${ENDPOINTS_MODEL.ERRORS}/${getPostfixWithModelName(modelType, modelName)}`,
				{
					params: {
						type: typeErrors,
					},
				}
			);
			return response.data;
		} catch (error) {
			if (error) {
				// возвращаем данные ошибки, пришедшие с бэка
				return rejectWithValue(error);
			}
		}
	}
);

// получение обработчика
export const getHandler = createAsyncThunk("model/getHandler", async ({ modelName, serviceType, modelType }: IGetHandlerProps, { rejectWithValue }) => {
	try {
		const response: AxiosResponse<string | IResponse> = await axiosApi.get(
			`${HOST}/${serviceType}/${ENDPOINTS_MODEL.HANDLER}/${getPostfixWithModelName(modelType, modelName)}`
		);
		return response.data;
	} catch (error) {
		if (error) {
			// возвращаем данные ошибки, пришедшие с бэка
			return rejectWithValue(error);
		}
	}
});

// загрузка обработчика
export const uploadHandler = createAsyncThunk(
	"model/uploadHandler",
	async ({ modelName, serviceType, modelType, formData }: IUploadHandlerProps, { rejectWithValue }) => {
		try {
			const response: AxiosResponse<IResponse> = await axiosApi.post(
				`${HOST}/${serviceType}/${ENDPOINTS_MODEL.HANDLER}/${getPostfixWithModelName(modelType, modelName)}`,
				{ handler: formData.get("filePy") },
				{
					headers: {
						"Content-Type": "multipart/form-data",
					},
				}
			);
			return response.data;
		} catch (error) {
			if (error) {
				// возвращаем данные ошибки, пришедшие с бэка
				return rejectWithValue(error);
			}
		}
	}
);

// удаление обработчика
export const deleteHandler = createAsyncThunk("model/deleteHandler", async ({ modelName, serviceType, modelType }: IDeleteHandlerProps, { rejectWithValue }) => {
	try {
		const response: AxiosResponse<IResponse> = await axiosApi.delete(
			`${HOST}/${serviceType}/${ENDPOINTS_MODEL.HANDLER}/${getPostfixWithModelName(modelType, modelName)}`
		);
		return response.data;
	} catch (error) {
		if (error) {
			// возвращаем данные ошибки, пришедшие с бэка
			return rejectWithValue(error);
		}
	}
});

const modelSlice = createSlice({
	name: "model",
	initialState,
	reducers: {
		// добавление имени модели
		addModelName: (state, action: PayloadAction<string | null>) => {
			state.fullModel.modelName = action.payload;
		},
		// добавление имени импортируемой модели
		addImportModelName: (state, action: PayloadAction<string>) => {
			state.modelImport.newName = action.payload;
		},
		// изменение прогресса импорта модели в %
		changeProgressImportModel: (state, action: PayloadAction<number>) => {
			state.modelImport.progress = Math.round(action.payload * 100);
		},
		// импорт loading Resumable
		loadingImportModel: (state) => {
			state.modelImport.status = RequestStatus.LOADING;
		},
		// импорт success Resumable
		successImportModel: (state) => {
			state.modelImport.status = RequestStatus.IDLE;
			state.modelImport.error = ResponseStatus.SUCCESS;
			state.modelImport.message = "Success";
		},
		// импорт failed Resumable
		failedImportModel: (state, action: PayloadAction<string>) => {
			state.modelImport.status = RequestStatus.FAILED;
			state.modelImport.error = ResponseStatus.FAILED;
			state.modelImport.message = action.payload;
		},
		// изменение активного типа модели
		changeActiveType: (state, action: PayloadAction<ModelType>) => {
			state.fullModel.activeType = action.payload;
		},
		// изменение статуса обучения модели
		changeTrainingStatus: (state, action: PayloadAction<boolean>) => {
			state.fullModel.training = action.payload;
		},
		// изменение статуса тестирования модели
		changeTestingStatus: (state, action: PayloadAction<boolean>) => {
			state.fullModel.testing = action.payload;
		},
		// очистка state
		clearState: (state) => {
			state.allModels = initialState.allModels;
			state.fullModel = initialState.fullModel;
			state.modelImport = initialState.modelImport;
			state.modelDelete = initialState.modelDelete;
			state.modelCopy = initialState.modelCopy;
			state.modelLogs = initialState.modelLogs;
			state.modelErrors = initialState.modelErrors;
		},
		// очистка статуса экспорта модели
		clearExportResponse: (state) => {
			state.modelExport = initialState.modelExport;
		},
		// очистка статуса импорта модели
		clearImportResponse: (state) => {
			state.modelImport = initialState.modelImport;
		},
		// очистка статуса удаления модели
		clearDeleteResponse: (state) => {
			state.modelDelete = initialState.modelDelete;
		},
		// очистка статуса копирования модели
		clearCopyModelResponse: (state) => {
			state.modelCopy = initialState.modelCopy;
		},
		// очистка логов модели
		clearLogs: (state) => {
			state.modelLogs = initialState.modelLogs;
		},
		// очистка ошибок модели
		clearErrors: (state) => {
			state.modelErrors = initialState.modelErrors;
		},
		// очистка обработчика
		clearHandler: (state) => {
			state.handler = initialState.handler;
		},
		// очистка статуса загрузки обработчика
		clearHandlerUpload: (state) => {
			state.handlerUpload = initialState.handlerUpload;
		},
		// очистка статуса удаления обработчика
		clearHandlerDelete: (state) => {
			state.handlerDelete = initialState.handlerDelete;
		},
		// очистка модели
		clearModel: (state) => {
			state.fullModel = initialState.fullModel;
		},
		// очистка всех моделей
		clearModels: (state) => {
			state.allModels = initialState.allModels;
			state.fullModel = initialState.fullModel;
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(getAllModels.pending, (state) => {
				state.allModels.status = RequestStatus.LOADING;
			})
			.addCase(getAllModels.fulfilled, (state, action) => {
				if (action.payload && typeof action.payload !== "string" && !("message" in action.payload)) {
					state.allModels.status = RequestStatus.IDLE;
					// создаем структуру
					const models: Record<string, IModelTypes> = {};
					action.payload.models.forEach((model) => {
						const modelName = model.replace(/(-new$)|(-backup$)/, "");
						if (!(modelName in models)) {
							models[modelName] = { future: null, current: null, previous: null };
						}
						if (/-new$/.test(model)) models[modelName].future = { status: ModelStatus.EMPTY };
						else if (/-backup$/.test(model)) models[modelName].previous = { status: ModelStatus.EMPTY };
						else models[modelName].current = { status: ModelStatus.EMPTY };
					});
					if (action.payload.handlers) {
						state.allModels.handlers = action.payload.handlers;
					} else {
						state.allModels.handlers = [];
					}
					if (action.meta.arg.additionalRequest) state.allModels.additionalModelsList = models; // доп.список
					else state.allModels.models = models;
					state.allModels.cleanListOfModels = action.payload.models.sort(); // чистый список моделей
					if ("pretrained" in action.payload && Array.isArray(action.payload.pretrained)) state.allModels.pretrainedModel = action.payload.pretrained.sort(); // список предустановленных моделей
				} else state.allModels.status = RequestStatus.FAILED;
			})
			.addCase(getAllModels.rejected, (state, action: PayloadAction<unknown>) => {
				state.allModels.status = RequestStatus.FAILED;
				if (action.payload instanceof AxiosError) {
					state.allModels.error = action.payload.response?.data.error;
					state.allModels.message = action.payload.response?.data.message;
				}
			})
			.addCase(getInfoModel.pending, (state, action) => {
				state.fullModel.status = RequestStatus.LOADING;
				if (action.meta.arg.modelName === state.fullModel.modelName) return; // не перезаписываем при запросе той же модели
				state.fullModel.modelName = action.meta.arg.modelName; // устанавливем имя активной модели
				// копируем структуру из списка моделей в активную модель
				if (action.meta.arg.serviceType === "smc" || action.meta.arg.serviceType === "see") {
					if (state.allModels.models && action.meta.arg.modelName in state.allModels.models) {
						state.fullModel.fullModel = state.allModels.models[action.meta.arg.modelName];
					}
				}
				// else {
				// 	if (state.allModels.additionalModelsList && action.meta.arg.modelName in state.allModels.additionalModelsList) {
				// 		state.fullModel.fullModel = state.allModels.additionalModelsList[action.meta.arg.modelName];
				// 	}
				// }
			})
			.addCase(getInfoModel.fulfilled, (state, action) => {
				state.fullModel.status = RequestStatus.IDLE;
				if (action.payload && typeof action.payload !== "string" && "status" in action.payload) {
					let activeType = state.fullModel.activeType;
					if (action.meta.arg.modelNameAsIs) {
						if (action.meta.arg.modelName.includes("-new")) activeType = "future";
						else if (action.meta.arg.modelName.includes("-backup")) activeType = "previous";
						else activeType = "current";
					}
					state.fullModel.fullModel[activeType] = action.payload;
				}
			})
			.addCase(getInfoModel.rejected, (state, action: PayloadAction<unknown>) => {
				state.fullModel.status = RequestStatus.FAILED;
				state.fullModel.fullModel[state.fullModel.activeType] = null;
				state.fullModel.testing = false;
				state.fullModel.training = false;
				if (action.payload instanceof AxiosError) {
					state.fullModel.error = action.payload.response?.data.error;
					state.fullModel.message = action.payload.response?.data.message;
				}
			})
			.addCase(exportModel.pending, (state) => {
				state.modelExport.status = RequestStatus.LOADING;
			})
			.addCase(exportModel.fulfilled, (state, action) => {
				state.modelExport.status = RequestStatus.IDLE;
				if (action.payload && "error" in action.payload) {
					state.modelExport.error = action.payload.error;
					state.modelExport.message = action.payload.message;
				}
			})
			.addCase(exportModel.rejected, (state, action: PayloadAction<unknown>) => {
				state.modelExport.status = RequestStatus.FAILED;
				if (action.payload instanceof AxiosError) {
					state.modelExport.error = action.payload.response?.data.error;
					state.modelExport.message = action.payload.response?.data.message;
				}
			})
			.addCase(importModel.pending, (state) => {
				state.modelImport.status = RequestStatus.LOADING;
			})
			.addCase(importModel.fulfilled, (state, action) => {
				state.modelImport.status = RequestStatus.IDLE;
				state.modelImport.error = action.payload.error;
				state.modelImport.message = action.payload.message;
			})
			.addCase(importModel.rejected, (state) => {
				state.modelImport.status = RequestStatus.FAILED;
			})
			.addCase(deleteModel.pending, (state) => {
				state.modelDelete.status = RequestStatus.LOADING;
			})
			.addCase(deleteModel.fulfilled, (state, action) => {
				state.modelDelete.status = RequestStatus.IDLE;
				if (action.payload) {
					state.modelDelete.error = action.payload.error;
					state.modelDelete.message = action.payload.message;
				}
			})
			.addCase(deleteModel.rejected, (state, action: PayloadAction<unknown>) => {
				state.modelDelete.status = RequestStatus.FAILED;
				if (action.payload instanceof AxiosError) {
					state.modelDelete.error = action.payload.response?.data.error;
					state.modelDelete.message = action.payload.response?.data.message;
				}
			})
			.addCase(copyModel.pending, (state) => {
				state.modelCopy.status = RequestStatus.LOADING;
			})
			.addCase(copyModel.fulfilled, (state, action) => {
				state.modelCopy.status = RequestStatus.IDLE;
				if (action.payload) {
					state.modelCopy.error = action.payload.error;
					state.modelCopy.message = action.payload.message;
				}
			})
			.addCase(copyModel.rejected, (state, action: PayloadAction<unknown>) => {
				state.modelCopy.status = RequestStatus.FAILED;
				if (action.payload instanceof AxiosError) {
					state.modelCopy.error = action.payload.response?.data.error;
					state.modelCopy.message = action.payload.response?.data.message;
				}
			})
			.addCase(getLogModel.pending, (state) => {
				state.modelLogs.status = RequestStatus.LOADING;
			})
			.addCase(getLogModel.fulfilled, (state, action) => {
				if (action.payload && "log" in action.payload) {
					state.modelLogs.status = RequestStatus.IDLE;
					state.modelLogs.logs = action.payload.log;
				} else state.modelLogs.status = RequestStatus.FAILED;
			})
			.addCase(getLogModel.rejected, (state, action: PayloadAction<unknown>) => {
				state.modelLogs.status = RequestStatus.FAILED;
				if (action.payload instanceof AxiosError) {
					state.modelLogs.error = action.payload.response?.data.error;
					state.modelLogs.message = action.payload.response?.data.message;
				}
			})
			.addCase(getErrorsModel.pending, (state) => {
				state.modelErrors.status = RequestStatus.LOADING;
			})
			.addCase(getErrorsModel.fulfilled, (state, action) => {
				if (Array.isArray(action.payload)) {
					state.modelErrors.status = RequestStatus.IDLE;
					state.modelErrors.errors = action.payload;
				} else state.modelErrors.status = RequestStatus.FAILED;
			})
			.addCase(getErrorsModel.rejected, (state, action: PayloadAction<unknown>) => {
				state.modelErrors.status = RequestStatus.FAILED;
				if (action.payload instanceof AxiosError) {
					state.modelErrors.error = action.payload.response?.data.error;
					state.modelErrors.message = action.payload.response?.data.message;
				}
			})
			.addCase(getHandler.pending, (state) => {
				state.handler.status = RequestStatus.LOADING;
			})
			.addCase(getHandler.fulfilled, (state, action) => {
				if (action.payload && typeof action.payload === "string" && !/<!doctype html>/i.test(action.payload)) {
					state.handler.status = RequestStatus.IDLE;
					state.handler.data = action.payload;
				} else state.handler.status = RequestStatus.FAILED;
			})
			.addCase(getHandler.rejected, (state, action: PayloadAction<unknown>) => {
				state.handler.status = RequestStatus.FAILED;
				if (action.payload instanceof AxiosError) {
					state.handler.data = action.payload.response?.data;
				}
			})
			.addCase(uploadHandler.pending, (state) => {
				state.handlerUpload.status = RequestStatus.LOADING;
			})
			.addCase(uploadHandler.fulfilled, (state, action) => {
				state.handlerUpload.status = RequestStatus.IDLE;
				if (action.payload) {
					state.handlerUpload.error = action.payload.error;
					state.handlerUpload.message = action.payload.message;
				}
			})
			.addCase(uploadHandler.rejected, (state, action: PayloadAction<unknown>) => {
				state.handlerUpload.status = RequestStatus.FAILED;
				if (action.payload instanceof AxiosError) {
					state.handlerUpload.error = action.payload.response?.data.error;
					state.handlerUpload.message = action.payload.response?.data.message;
				}
			})
			.addCase(deleteHandler.pending, (state) => {
				state.handlerDelete.status = RequestStatus.LOADING;
			})
			.addCase(deleteHandler.fulfilled, (state, action) => {
				state.handlerDelete.status = RequestStatus.IDLE;
				if (action.payload) {
					state.handlerDelete.error = action.payload.error;
					state.handlerDelete.message = action.payload.message;
				}
			})
			.addCase(deleteHandler.rejected, (state, action: PayloadAction<unknown>) => {
				state.handlerDelete.status = RequestStatus.FAILED;
				if (action.payload instanceof AxiosError) {
					state.handlerDelete.error = action.payload.response?.data.error;
					state.handlerDelete.message = action.payload.response?.data.message;
				}
			});
	},
});

export const {
	addModelName,
	addImportModelName,
	changeProgressImportModel,
	loadingImportModel,
	successImportModel,
	failedImportModel,
	changeActiveType,
	changeTrainingStatus,
	changeTestingStatus,
	clearState,
	clearExportResponse,
	clearImportResponse,
	clearDeleteResponse,
	clearCopyModelResponse,
	clearLogs,
	clearErrors,
	clearHandler,
	clearHandlerUpload,
	clearHandlerDelete,
	clearModel,
	clearModels,
} = modelSlice.actions;

export const selectAllModels = (state: RootState) => state.model.allModels;

export const selectFullModel = (state: RootState) => state.model.fullModel;
export const selectModelName = (state: RootState) => state.model.fullModel.modelName;
export const selectActiveType = (state: RootState) => state.model.fullModel.activeType;

export const selectExportStatus = (state: RootState) => state.model.modelExport;
export const selectImportStatus = (state: RootState) => state.model.modelImport;
export const selectDeleteStatus = (state: RootState) => state.model.modelDelete;
export const selectCopyModelStatus = (state: RootState) => state.model.modelCopy;

export const selectLogsModel = (state: RootState) => state.model.modelLogs;
export const selectErrorsModel = (state: RootState) => state.model.modelErrors;

export const selectHandler = (state: RootState) => state.model.handler;
export const selectUploadHandlerStatus = (state: RootState) => state.model.handlerUpload;
export const selectDeleteHandlerStatus = (state: RootState) => state.model.handlerDelete;

export default modelSlice.reducer;
